import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  styled,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useState } from 'react'
import { Property, Property_Office_Hour } from 'src/@types/graphql'
import { parse, format } from 'date-fns'
import { DateTime } from 'luxon'

function convertTimeRange(startTime, endTime) {
  const baseDate = new Date()

  // Ensure we have valid time values and convert to strings if needed
  const startTimeStr = String(startTime || '0').padStart(2, '0')
  const endTimeStr = String(endTime || '0').padStart(2, '0')

  try {
    const startDate = parse(startTimeStr, 'HH', baseDate)
    const endDate = parse(endTimeStr, 'HH', baseDate)

    const formattedStart = format(startDate, 'ha').toLowerCase()
    const formattedEnd = format(endDate, 'ha').toLowerCase()

    return `${formattedStart}-${formattedEnd}`
  } catch (error) {
    console.warn('Error parsing time range:', { startTime, endTime, error })
    return 'Invalid time'
  }
}

const StyledContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}))

const StyledSummaryContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.grey[50],
}))

const StyledCollapse = styled(Collapse)({
  marginLeft: 38,
})

const StyledCell = styled(TableCell)({
  padding: 6,
  border: 'none',
})

const getNormalizedOpenHours = (propertyOpenHours?: Property_Office_Hour[]) =>
  propertyOpenHours?.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.day_of_week]: curr.is_closed
        ? 'Closed'
        : convertTimeRange(curr.open_hour, curr.closed_hour),
    }),
    {
      MONDAY: 'Closed',
      TUESDAY: 'Closed',
      WEDNESDAY: 'Closed',
      THURSDAY: 'Closed',
      FRIDAY: 'Closed',
      SATURDAY: 'Closed',
      SUNDAY: 'Closed',
    },
  )

const getOpenCloseStatus = (openingHours = {}, timezone?: string | null) => {
  const currentDateTime = DateTime.now().setZone(timezone)
  const currentDay = currentDateTime.toFormat('EEEE').toUpperCase()

  if (openingHours[currentDay] === 'Closed') {
    return { open: false }
  }

  const [openTimeStr, closeTimeStr] =
    openingHours?.[currentDay]?.split('-') || []
  const openTime = openTimeStr?.trim()
    ? DateTime.fromFormat(openTimeStr?.trim(), 'ha', {
        zone: timezone,
      })
    : null
  const closeTime = closeTimeStr?.trim()
    ? DateTime.fromFormat(closeTimeStr?.trim(), 'ha', {
        zone: timezone,
      })
    : null
  if (currentDateTime >= openTime && currentDateTime < closeTime) {
    return { open: true, closes: closeTime.toFormat('ha').toLowerCase() }
  } else {
    return { open: false }
  }
}

const OfficeHours = ({ property }: { property?: Property }) => {
  const theme = useTheme()
  const officeHours = getNormalizedOpenHours(property?.property_office_hours)
  const [open, setOpen] = useState(false)

  const handleClick = () => {
    setOpen(!open)
  }

  const hours = [
    ...Object.entries(officeHours || {})
      .sort(([a], [b]) => {
        try {
          const parsedA = parse(a, 'EEEE', new Date())
          const parsedB = parse(b, 'EEEE', new Date())
          return parsedA.getDay() + 6 - parsedB.getDay()
        } catch (error) {
          console.warn('Error parsing day names:', { a, b, error })
          return 0
        }
      })
      .map(([day, hours]) => ({
        day,
        hours,
      })),
  ]

  const status = getOpenCloseStatus(officeHours, property?.timezone)

  return (
    <StyledContainer>
      <StyledSummaryContainer onClick={handleClick}>
        <Typography variant="body2">
          Hours:{' '}
          {open ? (
            ''
          ) : status.open ? (
            <>
              <Typography variant="body2" color="success.main" component="span">
                Open
              </Typography>{' '}
              - Closes {status.closes}
            </>
          ) : (
            <Typography variant="body2" color="error.main" component="span">
              Closed
            </Typography>
          )}
        </Typography>
        &nbsp;
        {open ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </StyledSummaryContainer>
      <StyledCollapse in={open}>
        <TableContainer>
          <Table aria-label="simple table">
            <TableBody>
              {hours.map((row, index) => (
                <TableRow key={index}>
                  <StyledCell component="th" scope="row">
                    {row.day}
                  </StyledCell>
                  <StyledCell>{row.hours}</StyledCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledCollapse>
    </StyledContainer>
  )
}

export default OfficeHours
